import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { useLocale } from "@/lib/LocaleContext";
import { useEntitlement } from "@/lib/useEntitlement";
import { localisedTo } from "@/lib/i18nRoutes";

/**
 * Hero card for the /music/free collection landing.
 *
 * Behaviour:
 *   - Logged out       : short signed-out prompt with sign-in CTA.
 *   - Balance > 0      : shows remaining count + rule explainer.
 *   - Balance = 0      : zero-balance CTA → Premium Collection.
 *   - Loading / error  : renders a subtle placeholder that keeps layout stable.
 *
 * All copy comes from `music.balance.*` i18n keys — Arabic strings included.
 * Visual language matches the existing collection kicker/title/lead style.
 */
export default function FreeAllowancePanel() {
  const { user } = useAuth();
  const { lang } = useLocale();
  const { t } = useTranslation();
  const { summary, loading } = useEntitlement();

  const remaining = Number(summary?.remaining ?? 0);
  const zero = user && summary && remaining === 0;

  // ------------------------------------------------------------------
  // Logged-out state — do not disclose account-specific data.
  // ------------------------------------------------------------------
  if (!user) {
    return (
      <div
        data-testid="free-allowance-panel-logged-out"
        className="border border-white/10 bg-white/[0.02] rounded-md p-5 sm:p-6"
      >
        <div className="text-[10px] tracking-[0.3em] text-white/40 mb-2">
          {t("music.balance.kicker")}
        </div>
        <h3 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("music.balance.loggedOutTitle")}
        </h3>
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          {t("music.balance.rule")}
        </p>
        <Link
          to={localisedTo("/auth", lang)}
          data-testid="free-allowance-signin-cta"
          className="btn-primary !text-xs !py-2 !px-4"
        >
          {t("music.balance.signInCta")}
        </Link>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Loading — placeholder that keeps layout stable.
  // ------------------------------------------------------------------
  if (loading || !summary) {
    return (
      <div
        data-testid="free-allowance-panel-loading"
        className="border border-white/10 bg-white/[0.02] rounded-md p-5 sm:p-6 min-h-[7.5rem]"
        aria-hidden
      >
        <div className="text-[10px] tracking-[0.3em] text-white/30 mb-2">
          {t("music.balance.kicker")}
        </div>
        <div className="h-6 w-40 bg-white/5 rounded animate-pulse mb-3" />
        <div className="h-4 w-3/4 bg-white/5 rounded animate-pulse" />
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Zero-balance state — CTA into Premium Collection.
  // ------------------------------------------------------------------
  if (zero) {
    return (
      <div
        data-testid="free-allowance-panel-zero"
        className="border border-red-brand/30 bg-red-brand/[0.04] rounded-md p-5 sm:p-6"
      >
        <div className="text-[10px] tracking-[0.3em] text-red-brand mb-2">
          {t("music.balance.zeroKicker")}
        </div>
        <h3 className="font-display text-xl sm:text-2xl text-white mb-2">
          {t("music.balance.zeroTitle", { count: summary.next_unlock_amount })}
        </h3>
        <p className="text-white/70 text-sm leading-relaxed mb-4">
          {t("music.balance.zeroBody", { count: summary.next_unlock_amount })}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={localisedTo("/music/premium", lang)}
            data-testid="free-allowance-premium-cta"
            className="btn-primary !text-xs !py-2 !px-4"
          >
            {t("music.balance.zeroCta")}
          </Link>
          <div className="text-[10px] tracking-[0.2em] text-white/40 font-mono">
            {t("music.balance.reDownloadHint")}
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Positive balance — show remaining count + explainer.
  // ------------------------------------------------------------------
  return (
    <div
      data-testid="free-allowance-panel-positive"
      data-testid-remaining={remaining}
      className="border border-white/10 bg-white/[0.02] rounded-md p-5 sm:p-6"
    >
      <div className="text-[10px] tracking-[0.3em] text-white/40 mb-2">
        {t("music.balance.kicker")}
      </div>
      <div className="flex items-baseline gap-3 mb-2">
        <span
          className="font-anton text-4xl sm:text-5xl text-white leading-none"
          dir="ltr"
        >
          {remaining}
        </span>
        <span className="text-white/60 text-sm">
          {remaining === 1
            ? t("music.balance.remainingOne")
            : t("music.balance.remainingMany")}
        </span>
      </div>
      <p className="text-white/60 text-sm leading-relaxed">
        {t("music.balance.rule")}
      </p>
    </div>
  );
}
